import axios from 'axios';
import { EncoderScriptTemplates } from '@mimir/client-common/model/encoderExporterTypes';
import escapeRegExp from 'lodash/escapeRegExp';
import type { ElectronContext } from '@mimir/common/context/applicationContext';

/**
 * Fetches and caches the JSON definition from ${siteRoot}/assets/premiere/json2.min.js
 */
let jsonDefinitionString = '';
const getJsonDefinition = async () => {
  if (jsonDefinitionString) {
    return jsonDefinitionString;
  }

  const siteRoot = window.location.origin;
  const timestamp = new Date().getTime();
  console.debug(`siteRoot: ${siteRoot}`);

  const json2Response = await axios.get(`${siteRoot}/assets/premiere/json2.min.js?ts=${timestamp}`);
  jsonDefinitionString = json2Response.data;

  return jsonDefinitionString;
};

let ameExporterScriptString = '';
const getAmeExporterScriptString = async () => {
  if (ameExporterScriptString) {
    return ameExporterScriptString;
  }

  const siteRoot = window.location.origin;
  const timestamp = new Date().getTime();
  console.debug(`siteRoot: ${siteRoot}`);

  const ameExporterResponse = await axios.get(`${siteRoot}/assets/ameExporter.jsx?ts=${timestamp}`);
  ameExporterScriptString = ameExporterResponse.data;

  return ameExporterScriptString;
};

/**
 * We need to add extra escape characters to path strings being used in the AME script string for Windows.
 */
function getPathForScript(electronContext: ElectronContext, pathForScript: string) {
  let result = pathForScript;
  if (result && electronContext.isWindows) {
    result = result.replace(/\\/g, '\\\\');
    result = result.replace(/\//g, '\\\\');
  }

  return result;
}

/**
 * Builds a custom ExtendScript for the given params and returns the string so that it can be written as a
 * disk file to be pointed to from the AME launch command. The script starts the encoding for the given
 * .prproj source file, and writes progress/error/completion info to a disk file.
 */
export const getAmeScriptString = async (applicationContext: ElectronContext, encoderExePath: string, itemId: string, projectPath: string, presetPath: string, outputPath: string, progressFilePath: string) => {
  /**
   * We need JSON.stringify to make it easier to serialize progress updates while writing to a disk file,
   * especially for escaping the log string since that is returned as a JSON itself. This is why we are
   * injecting our own JSON definition (that we already have for Premiere scripting) into the script.
   */

  const jsonDefinition = await getJsonDefinition();

  // Construct the AME script that launches the render job and writes progress to disk by first fetching the
  // static script code and then replacing the placeholders for dynamic variables with their actual value
  let ameExporterScript = await getAmeExporterScriptString();
  const replacements = {
    [EncoderScriptTemplates.SCRIPT_SOURCE_PATH_TEMPLATE]: getPathForScript(applicationContext, projectPath),
    [EncoderScriptTemplates.SCRIPT_PRESET_PATH_TEMPLATE]: getPathForScript(applicationContext, presetPath),
    [EncoderScriptTemplates.SCRIPT_DESTINATION_PATH_TEMPLATE]: getPathForScript(applicationContext, outputPath),
    [EncoderScriptTemplates.SCRIPT_PROGRESS_PATH_TEMPLATE]: getPathForScript(applicationContext, progressFilePath),
    [EncoderScriptTemplates.ITEM_ID_TEMPLATE]: itemId,
  };
  const replacementPattern = new RegExp(
    Object.keys(replacements)
      .map(key => escapeRegExp(key))
      .join('|'),
    'g'
  );
  ameExporterScript = ameExporterScript.replace(
    replacementPattern,
    (match) => replacements[match as keyof typeof replacements] || ''
  );

  return `
    ${jsonDefinition}

    ${ameExporterScript}
  `;
};
